import {
  ActiveFilter,
  CriterionNumberFilterValue,
  Filter,
  FilterLinker,
  SelectFilterOption,
  ServiceTypeTab,
} from "@/types/filter";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { userLoggedOut } from "../auth/authSlice";
import { DefaultErrorGroups, ErrorGroupFilter } from "@api/core/logistics/sales-orders/sales-orders.schema";
import { DEFAULT_STATUS_FILTERS, ORDER_STATUS_CODE_TO_NAME } from "@/consts/salesOrderConsts";

type SetOrderFiltersPayload = {
  filters: Record<string, Filter>;
};

type SetSingleSelectActiveFilterValuePayload = {
  index: number;
  item: SelectFilterOption;
};

type UpdateCriterionNumberFilterPayload = {
  index: number;
  value: CriterionNumberFilterValue;
};

type UpdateFilterLinkerPayload = {
  index: number;
  value: FilterLinker;
};

type InitializeErrorGroupStatusFiltersPayload = {
  errorGroups: ErrorGroupFilter["ErrorGroups"];
};

type ToggleErrorGroupStatusFilterPayload = {
  errorGroupName: string | DefaultErrorGroups;
  filter: string;
};

export type ServiceTypeTabsWithErrorGroups = ServiceTypeTab.FULFILLMENT | ServiceTypeTab.FREIGHT | ServiceTypeTab.PARCEL | ServiceTypeTab.FULFILLMENT_DTC | ServiceTypeTab.FULFILLMENT_B2B | ServiceTypeTab.DTC;

type SetSelectedErrorGroupPayload = {
  tab: ServiceTypeTabsWithErrorGroups;
  errorGroup: string | DefaultErrorGroups | undefined;
};

type OrderFiltersState = {
  filters: Record<string, Filter> | undefined;
  activeFilters: ActiveFilter[];
  filterLinkers: FilterLinker[];
  searchString: string;
  currentQuery: string[][];
  orderStatusFilters: string[];
  errorGroupsByTab: Record<ServiceTypeTabsWithErrorGroups, string | DefaultErrorGroups | undefined>;
  errorGroupStatusFilters: Record<string | DefaultErrorGroups, string[]>;
};

const initialState: OrderFiltersState = {
  filters: undefined,
  activeFilters: [],
  filterLinkers: [],
  searchString: "",
  currentQuery: [],
  orderStatusFilters: [],
  errorGroupsByTab: {
    [ServiceTypeTab.FULFILLMENT]: undefined,
    [ServiceTypeTab.FREIGHT]: undefined,
    [ServiceTypeTab.PARCEL]: undefined,
    [ServiceTypeTab.FULFILLMENT_DTC]: undefined,
    [ServiceTypeTab.FULFILLMENT_B2B]: undefined,
    [ServiceTypeTab.DTC]: undefined,
  },
  errorGroupStatusFilters: {},
};

export const orderFiltersSlice = createSlice({
  name: "orderFilters",
  initialState,
  reducers: {
    setOrderFilters: (state, action: PayloadAction<SetOrderFiltersPayload>) => {
      state.filters = action.payload.filters;
    },
    setOrderSearchString: (state, action: PayloadAction<string>) => {
      state.searchString = action.payload;
    },
    setSelectedErrorGroup: (state, action: PayloadAction<SetSelectedErrorGroupPayload>) => {
      state.errorGroupsByTab[action.payload.tab] = action.payload.errorGroup;
    },
    clearAllErrorGroups: (state) => {
      state.errorGroupsByTab = {
        [ServiceTypeTab.FULFILLMENT]: undefined,
        [ServiceTypeTab.FREIGHT]: undefined,
        [ServiceTypeTab.PARCEL]: undefined,
        [ServiceTypeTab.FULFILLMENT_DTC]: undefined,
        [ServiceTypeTab.FULFILLMENT_B2B]: undefined,
        [ServiceTypeTab.DTC]: undefined,
      };
    },
    appendOrderFilter: (state, action: PayloadAction<ActiveFilter>) => {
      state.activeFilters.push(action.payload);

      if (state.activeFilters.length !== 0) {
        state.filterLinkers.push(FilterLinker.OR);
      }
    },
    appendOrderFilterWithLinker: (
      state,
      action: PayloadAction<{
        filter: ActiveFilter;
        linker?: FilterLinker;
      }>
    ) => {
      if (state.activeFilters.length !== 0) {
        state.filterLinkers.push(action.payload.linker || FilterLinker.AND);
      }
      state.activeFilters.push(action.payload.filter);
    },
    setOrderFiltersAndLinkers: (
      state,
      action: PayloadAction<{
        filters: ActiveFilter[];
        linkers: FilterLinker[];
      }>
    ) => {
      state.activeFilters = action.payload.filters;
      state.filterLinkers = action.payload.linkers;
    },
    setOrderSingleSelectActiveFilterValue: (
      state,
      action: PayloadAction<SetSingleSelectActiveFilterValuePayload>
    ) => {
      state.activeFilters[action.payload.index].filter = action.payload.item;
    },
    updateOrderCriterionNumberFilter: (
      state,
      action: PayloadAction<UpdateCriterionNumberFilterPayload>
    ) => {
      const currentFilterValue = state.activeFilters[action.payload.index];
      if (currentFilterValue) {
        if (!currentFilterValue.filter) {
          currentFilterValue.filter = action.payload.value;
          return;
        }
        if ("criteria" in action.payload.value) {
          (currentFilterValue.filter as CriterionNumberFilterValue).criteria =
            action.payload.value.criteria;
        }
        if ("value" in action.payload.value) {
          (currentFilterValue.filter as CriterionNumberFilterValue).value =
            action.payload.value.value;
        }
      }
    },
    updateOrderFilterLinkerWithIndex: (
      state,
      action: PayloadAction<UpdateFilterLinkerPayload>
    ) => {
      state.filterLinkers[action.payload.index] = action.payload.value;
    },
    removeOrderActiveFilterWithIndex: (
      state,
      action: PayloadAction<number>
    ) => {
      // Remove linker
      const filterIndex = action.payload;
      if (filterIndex === state.activeFilters.length - 1) {
        state.filterLinkers.pop();
      } else if (filterIndex >= 0) {
        state.filterLinkers.splice(filterIndex, 1);
      }

      state.activeFilters.splice(filterIndex, 1);
    },
    clearAllOrderActiveFilters: (state) => {
      state.activeFilters = [];
      state.filterLinkers = [];
    },
    setCurrentOrderQuery: (state, action: PayloadAction<string[][]>) => {
      state.currentQuery = action.payload;
    },
    toggleOrderStatusFilter: (state, action: PayloadAction<string>) => {
      if (state.orderStatusFilters.includes(action.payload)) {
        state.orderStatusFilters = state.orderStatusFilters.filter(item => item !== action.payload);
      } else {
        state.orderStatusFilters.push(action.payload);
      }
    },
    setOrderStatusFilters: (state, action: PayloadAction<string[]>) => {
      state.orderStatusFilters = action.payload;
    },
    initializeErrorGroupStatusFilters: (state, action: PayloadAction<InitializeErrorGroupStatusFiltersPayload>) => {
      action.payload.errorGroups.forEach((group) => {
        // Only initialize if key doesn't exist - preserve user's filter selections
        if (state.errorGroupStatusFilters[group.errorGroupName] === undefined) {
          if (group.errorGroupName === DefaultErrorGroups.ALL_NON_CREDITABLE) {
            state.errorGroupStatusFilters[group.errorGroupName] = [ORDER_STATUS_CODE_TO_NAME.NO_REVIEW_REQUIRED];
            return;
          }
          state.errorGroupStatusFilters[group.errorGroupName] = DEFAULT_STATUS_FILTERS;
        }
      });
      if (state.errorGroupStatusFilters[DefaultErrorGroups.ALL_ORDERS] === undefined) {
        state.errorGroupStatusFilters[DefaultErrorGroups.ALL_ORDERS] = Object.values(ORDER_STATUS_CODE_TO_NAME);
      }
    },
    toggleErrorGroupStatusFilter: (state, action: PayloadAction<ToggleErrorGroupStatusFilterPayload>) => {
      const index = state.errorGroupStatusFilters[action.payload.errorGroupName].indexOf(action.payload.filter);
      if (index >= 0) {
        state.errorGroupStatusFilters[action.payload.errorGroupName].splice(index, 1);
      } else {
        state.errorGroupStatusFilters[action.payload.errorGroupName].push(action.payload.filter);
      }
    },
    clearErrorGroupStatusFilters: (state) => {
      state.errorGroupStatusFilters = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userLoggedOut, (_) => {
      return initialState;
    })
  },
});

export const {
  setOrderFilters,
  setOrderSearchString,
  appendOrderFilter,
  updateOrderCriterionNumberFilter,
  setOrderSingleSelectActiveFilterValue,
  updateOrderFilterLinkerWithIndex,
  removeOrderActiveFilterWithIndex,
  clearAllOrderActiveFilters,
  appendOrderFilterWithLinker,
  setOrderFiltersAndLinkers,
  setCurrentOrderQuery,
  toggleOrderStatusFilter,
  setOrderStatusFilters,
  setSelectedErrorGroup,
  clearAllErrorGroups,
  initializeErrorGroupStatusFilters,
  toggleErrorGroupStatusFilter,
  clearErrorGroupStatusFilters,
} = orderFiltersSlice.actions;

export const selectOrderFilters = (state: RootState) =>
  state.orderFilters.filters;
export const selectActiveOrderFilters = (state: RootState) =>
  state.orderFilters.activeFilters;
export const selectOrderSearchString = (state: RootState) =>
  state.orderFilters.searchString;
export const selectOrderFilterLinkers = (state: RootState) =>
  state.orderFilters.filterLinkers;
export const selectCurrentOrderQuery = (state: RootState) =>
  state.orderFilters.currentQuery;
export const selectOrderStatusFilters = (state: RootState) =>
  state.orderFilters.orderStatusFilters;
export const selectErrorGroup = (state: RootState) => {
  // Import selectCurrentServiceTab here to avoid circular dependencies
  const currentTab = state.serviceTab.currentServiceTab;
  if (!currentTab || currentTab === ServiceTypeTab.SERVICE_DETAILS) {
    return undefined;
  }
  return state.orderFilters.errorGroupsByTab[currentTab as ServiceTypeTabsWithErrorGroups];
};

export const selectErrorGroupForTab = (tab: ServiceTypeTabsWithErrorGroups) => (state: RootState) =>
  state.orderFilters.errorGroupsByTab[tab];

export const selectAllErrorGroupsByTab = (state: RootState) =>
  state.orderFilters.errorGroupsByTab;
export const selectErrorGroupStatusFilters = (state: RootState) =>
  state.orderFilters.errorGroupStatusFilters;

export default orderFiltersSlice.reducer;
